import { useCallback, useEffect, useState } from "react";
import { toast } from "sonner";
import api, { formatApiError, money } from "@/lib/api";
import { PageHeader, EmptyState, StatCard } from "@/components/common";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
  Table, TableBody, TableCell, TableHead, TableHeader, TableRow,
} from "@/components/ui/table";
import {
  PaintBrush, FloppyDisk, LinkSimple, CheckCircle, WarningCircle, ArrowsClockwise, CalendarBlank,
} from "@phosphor-icons/react";

const fmtDate = (d) => (d ? new Date(d).toLocaleString() : "—");
const fmtDay = (d) => {
  if (!d) return "—";
  const dt = new Date(d);
  if (Number.isNaN(dt.getTime())) return "—";
  return dt.toLocaleDateString(undefined, {
    year: "numeric", month: "short", day: "numeric", timeZone: "UTC",
  }) + " UTC";
};
const TYPE_LABEL = {
  white_label_charge: "Activation",
  white_label_renewal: "Monthly renewal",
};

const emptyForm = {
  product_name: "",
  slogan: "",
  website_url: "",
  website_title: "",
};

export default function WhiteLabeling() {
  const [info, setInfo] = useState(null);
  const [payments, setPayments] = useState([]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [retryingSsl, setRetryingSsl] = useState(false);
  const [renewing, setRenewing] = useState(false);
  const [form, setForm] = useState(emptyForm);
  const [logo, setLogo] = useState(null);
  const [loginLogo, setLoginLogo] = useState(null);
  const [favicon, setFavicon] = useState(null);
  const [logoPreview, setLogoPreview] = useState(null);
  const [loginLogoPreview, setLoginLogoPreview] = useState(null);
  const [faviconPreview, setFaviconPreview] = useState(null);
  const [clearLoginLogo, setClearLoginLogo] = useState(false);

  const refreshBalance = useCallback(async () => {
    try {
      const { data } = await api.get("/billing/balance");
      const bal = data?.cash_balance_minor ?? 0;
      setInfo((prev) => (prev ? { ...prev, cash_balance_minor: bal } : prev));
      return bal;
    } catch {
      return null;
    }
  }, []);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const [me, pay, bal] = await Promise.all([
        api.get("/white-label/me"),
        api.get("/white-label/payments"),
        api.get("/billing/balance").catch(() => null),
      ]);
      const cash = bal?.data?.cash_balance_minor ?? me.data?.cash_balance_minor ?? 0;
      setInfo({ ...me.data, cash_balance_minor: cash });
      setPayments(pay.data || []);
      if (me.data?.active) {
        setForm({
          product_name: me.data.product_name || "",
          slogan: me.data.slogan || me.data.tagline || "",
          website_url: me.data.website_url || me.data.domain || "",
          website_title: me.data.website_title || "",
        });
        setLogoPreview(me.data.logo_url || null);
        setLoginLogoPreview(me.data.login_logo_url || null);
        setFaviconPreview(me.data.favicon_url || null);
        setLoginLogo(null);
        setClearLoginLogo(false);
      }
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail));
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { load(); }, [load]);

  // Keep cash card in sync with header (credits / charges while this page is open).
  useEffect(() => {
    const id = setInterval(refreshBalance, 30000);
    const onFocus = () => refreshBalance();
    window.addEventListener("focus", onFocus);
    return () => {
      clearInterval(id);
      window.removeEventListener("focus", onFocus);
    };
  }, [refreshBalance]);

  const onFile = (file, setFile, setPreview) => {
    setFile(file || null);
    if (file) setPreview(URL.createObjectURL(file));
  };

  const activate = async () => {
    if (!form.product_name.trim() || !form.website_url.trim() || !form.website_title.trim()) {
      toast.error("Product name, website URL, and website title are required.");
      return;
    }
    if (!logo && !info?.logo_url) {
      toast.error("Upload a logo.");
      return;
    }
    if (!favicon && !info?.favicon_url) {
      toast.error("Upload a favicon.");
      return;
    }
    // Re-activation / first activation always requires fresh uploads for simplicity.
    if (!logo || !favicon) {
      toast.error("Please select both logo and favicon files to activate or renew branding.");
      return;
    }
    const price = info?.price_minor;
    if (price == null) {
      toast.error("White label price is not set. Contact admin.");
      return;
    }
    // Live balance — page state can be stale after a credit (header polls separately).
    const liveBal = await refreshBalance();
    const cash = liveBal ?? info?.cash_balance_minor ?? 0;
    if (cash < price && info?.status !== "active") {
      toast.error(`Insufficient balance. Need ${money(price)} to activate.`);
      return;
    }
    setSaving(true);
    try {
      const body = new FormData();
      body.append("product_name", form.product_name.trim());
      body.append("slogan", form.slogan.trim());
      body.append("website_url", form.website_url.trim());
      body.append("website_title", form.website_title.trim());
      body.append("logo", logo);
      body.append("favicon", favicon);
      if (loginLogo) body.append("login_logo", loginLogo);
      if (clearLoginLogo) body.append("clear_login_logo", "1");
      // Let the browser set multipart boundary — do not force Content-Type.
      const { data } = await api.post("/white-label/activate", body);
      setInfo(data);
      if (data.charged_minor > 0) {
        toast.success(`White labeling activated · charged ${money(data.charged_minor)}`);
      } else {
        toast.success("White labeling branding updated");
      }
      if (data.ssl_status === "failed") {
        toast.message(`SSL: ${data.ssl_message || "failed — check DNS points to this server"}`);
      }
      setLogo(null);
      setLoginLogo(null);
      setFavicon(null);
      setClearLoginLogo(false);
      load();
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail));
      refreshBalance();
    } finally {
      setSaving(false);
    }
  };

  const retrySsl = async () => {
    setRetryingSsl(true);
    try {
      const { data } = await api.post("/white-label/retry-ssl");
      setInfo((prev) => ({ ...prev, ...data }));
      if (data.ssl_status === "ready") {
        toast.success("SSL certificate installed.");
      } else if (data.ssl_status === "skipped") {
        toast.message(data.ssl_message || "SSL skipped");
      } else {
        toast.error(data.ssl_message || "SSL failed — check DNS points to this server");
      }
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail));
    } finally {
      setRetryingSsl(false);
    }
  };

  const renewNow = async () => {
    const priceNeed = info?.price_minor;
    if (priceNeed == null) {
      toast.error("White label price is not set. Contact admin.");
      return;
    }
    const liveBal = await refreshBalance();
    const cash = liveBal ?? info?.cash_balance_minor ?? 0;
    if (cash < priceNeed) {
      toast.error(`Insufficient balance. Need ${money(priceNeed)} to renew.`);
      return;
    }
    setRenewing(true);
    try {
      const { data } = await api.post("/white-label/renew");
      setInfo((prev) => ({ ...prev, ...data }));
      toast.success(
        `Renewed through ${data.renewed_period || "next period"} · charged ${money(data.charged_minor || priceNeed)}`,
      );
      load();
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail));
      refreshBalance();
    } finally {
      setRenewing(false);
    }
  };

  const status = info?.status || "inactive";
  const price = info?.price_minor;
  const showSchedule = (status === "active" || status === "lapsed") && (info?.expires_at || info?.next_renewal_at);

  return (
    <div className="space-y-6" data-testid="white-labeling-page">
      <PageHeader
        title="White labeling"
        subtitle="Brand your portal with your product name, domain, logo, and favicon. Charged monthly from your cash balance."
      />

      {loading ? (
        <p className="text-sm text-muted-foreground">Loading…</p>
      ) : (
        <>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
            <StatCard
              testid="wl-price"
              icon={PaintBrush}
              tone="primary"
              label="White label price"
              value={price != null ? `${money(price)} / mo` : "Not set"}
              hint="Charged on activation and each monthly renewal"
            />
            <StatCard
              testid="wl-status"
              icon={status === "active" ? CheckCircle : WarningCircle}
              tone={status === "active" ? "success" : status === "lapsed" ? "warning" : "muted"}
              label="Status"
              value={status === "active" ? "Active" : status === "lapsed" ? "Lapsed" : "Inactive"}
              hint={
                status === "lapsed"
                  ? `Expired ${fmtDay(info?.expires_at)} · renew to restore branding`
                  : status === "active"
                    ? `Expires ${fmtDay(info?.expires_at)} · next renewal ${fmtDay(info?.next_renewal_at)}`
                    : "Complete the form below to activate"
              }
            />
            <StatCard
              testid="wl-balance"
              icon={LinkSimple}
              tone="success"
              label="Cash balance"
              value={money(info?.cash_balance_minor ?? 0)}
              hint="Must cover one month of white labeling to activate or renew"
            />
          </div>

          {showSchedule && (
            <div
              className="rounded-xl border border-border bg-card card-shadow p-5"
              data-testid="wl-renewal-panel"
            >
              <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
                <div className="flex items-start gap-3 min-w-0">
                  <div className="w-11 h-11 rounded-lg grid place-items-center shrink-0 text-primary bg-accent">
                    <CalendarBlank className="w-5 h-5" weight="duotone" />
                  </div>
                  <div className="min-w-0 space-y-2">
                    <h3 className="font-semibold text-base">Subscription period</h3>
                    <div className="grid grid-cols-1 sm:grid-cols-2 gap-x-8 gap-y-1 text-sm">
                      <p data-testid="wl-expires-at">
                        <span className="text-muted-foreground">Expires </span>
                        <span className="font-medium tabular-nums">{fmtDay(info.expires_at)}</span>
                        {info.last_period && (
                          <span className="text-muted-foreground"> ({info.last_period})</span>
                        )}
                      </p>
                      <p data-testid="wl-next-renewal-at">
                        <span className="text-muted-foreground">Next renewal </span>
                        <span className="font-medium tabular-nums">{fmtDay(info.next_renewal_at)}</span>
                        {info.next_period && (
                          <span className="text-muted-foreground"> ({info.next_period})</span>
                        )}
                      </p>
                    </div>
                    <p className="text-xs text-muted-foreground">
                      {status === "lapsed"
                        ? "Branding is offline until you renew. Charge is taken from your cash balance."
                        : info.can_renew_now
                          ? `Renew early for ${info.renew_period || "next month"} — charged ${price != null ? money(price) : "—"} from cash balance.`
                          : "Already prepaid through the next period."}
                    </p>
                  </div>
                </div>
                {info.can_renew_now && (
                  <Button
                    onClick={renewNow}
                    disabled={renewing || saving || price == null}
                    className="rounded-lg shrink-0"
                    data-testid="wl-renew-btn"
                  >
                    <ArrowsClockwise className={`w-4 h-4 mr-1.5 ${renewing ? "animate-spin" : ""}`} weight="bold" />
                    {renewing
                      ? "Renewing…"
                      : `Renew now · ${price != null ? money(price) : "—"}`}
                  </Button>
                )}
              </div>
            </div>
          )}

          <div className="rounded-xl border border-border bg-card card-shadow p-5 space-y-4" data-testid="wl-activate-form">
            <div>
              <h3 className="font-semibold text-base">Activation</h3>
              <p className="text-sm text-muted-foreground mt-0.5">
                Provide branding details, then activate. We install SSL for your domain and show your brand on that site.
                {price != null && status !== "active" && (
                  <> Your balance will be charged <span className="font-medium text-foreground">{money(price)}</span>.</>
                )}
              </p>
            </div>

            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
              <div className="space-y-1.5">
                <Label>Product name</Label>
                <Input
                  data-testid="wl-product-name"
                  value={form.product_name}
                  onChange={(e) => setForm({ ...form, product_name: e.target.value })}
                  placeholder="Acme Voice"
                  maxLength={80}
                />
              </div>
              <div className="space-y-1.5">
                <Label>Slogan</Label>
                <Input
                  data-testid="wl-slogan"
                  value={form.slogan}
                  onChange={(e) => setForm({ ...form, slogan: e.target.value })}
                  placeholder="AI Voice Agents"
                  maxLength={120}
                />
              </div>
              <div className="space-y-1.5">
                <Label>Website title</Label>
                <Input
                  data-testid="wl-website-title"
                  value={form.website_title}
                  onChange={(e) => setForm({ ...form, website_title: e.target.value })}
                  placeholder="Acme Voice — AI Calling"
                  maxLength={120}
                />
              </div>
              <div className="space-y-1.5 sm:col-span-2">
                <Label>Website URL</Label>
                <Input
                  data-testid="wl-website-url"
                  value={form.website_url}
                  onChange={(e) => setForm({ ...form, website_url: e.target.value })}
                  placeholder="https://portal.example.com"
                />
                <p className="text-[11px] text-muted-foreground">
                  Point this domain&apos;s DNS A record to this server before activating so SSL can be issued.
                </p>
              </div>
              <div className="space-y-1.5">
                <Label>Logo</Label>
                <Input
                  data-testid="wl-logo"
                  type="file"
                  accept="image/png,image/jpeg,image/webp,image/gif,image/svg+xml"
                  onChange={(e) => onFile(e.target.files?.[0], setLogo, setLogoPreview)}
                />
                {logoPreview && (
                  <img src={logoPreview} alt="Logo preview" className="mt-2 h-12 object-contain" />
                )}
              </div>
              <div className="space-y-1.5">
                <Label>Login / signup logo</Label>
                <Input
                  data-testid="wl-login-logo"
                  type="file"
                  accept="image/png,image/jpeg,image/webp,image/gif,image/svg+xml"
                  onChange={(e) => {
                    const f = e.target.files?.[0];
                    setClearLoginLogo(false);
                    onFile(f, setLoginLogo, setLoginLogoPreview);
                  }}
                />
                <p className="text-[11px] text-muted-foreground">
                  Shown on the login and signup pages. Use a light or white mark for the purple panel. Falls back to the main logo if empty.
                </p>
                {loginLogoPreview && !clearLoginLogo && (
                  <div className="mt-2 flex items-center gap-3 rounded-lg bg-primary p-3">
                    <img src={loginLogoPreview} alt="Login logo preview" className="h-12 object-contain" />
                    <Button
                      type="button"
                      variant="ghost"
                      size="sm"
                      className="text-xs text-primary-foreground hover:bg-white/10 hover:text-primary-foreground"
                      onClick={() => { setLoginLogo(null); setLoginLogoPreview(null); setClearLoginLogo(true); }}
                      data-testid="wl-login-logo-remove"
                    >
                      Remove
                    </Button>
                  </div>
                )}
              </div>
              <div className="space-y-1.5">
                <Label>Favicon</Label>
                <Input
                  data-testid="wl-favicon"
                  type="file"
                  accept="image/png,image/jpeg,image/webp,image/x-icon,image/vnd.microsoft.icon,image/svg+xml,.ico"
                  onChange={(e) => onFile(e.target.files?.[0], setFavicon, setFaviconPreview)}
                />
                {faviconPreview && (
                  <img src={faviconPreview} alt="Favicon preview" className="mt-2 h-8 w-8 object-contain" />
                )}
              </div>
            </div>

            {info?.ssl_message && (
              <p className={`text-sm ${info.ssl_status === "failed" ? "text-amber-700" : "text-muted-foreground"}`}>
                SSL: {info.ssl_message}
              </p>
            )}

            <div className="flex flex-wrap gap-2">
              <Button
                onClick={activate}
                disabled={saving || price == null}
                className="rounded-lg"
                data-testid="wl-activate-btn"
              >
                <FloppyDisk className="w-4 h-4 mr-1.5" weight="bold" />
                {saving
                  ? "Activating…"
                  : status === "active"
                    ? "Update branding"
                    : `Activate · ${price != null ? money(price) : "—"}`}
              </Button>
              {(status === "active" || status === "lapsed") && info?.ssl_status === "failed" && (
                <Button
                  variant="outline"
                  onClick={retrySsl}
                  disabled={retryingSsl || saving}
                  className="rounded-lg"
                  data-testid="wl-retry-ssl-btn"
                >
                  {retryingSsl ? "Retrying SSL…" : "Retry SSL"}
                </Button>
              )}
            </div>
          </div>

          <div className="rounded-xl border border-border bg-card card-shadow overflow-hidden" data-testid="wl-payment-history">
            <div className="px-5 py-3.5 border-b border-border">
              <h3 className="font-semibold text-base">White labeling payment history</h3>
              <p className="text-sm text-muted-foreground">Activation and monthly renewal charges.</p>
            </div>
            {payments.length === 0 ? (
              <EmptyState
                testid="wl-payments-empty"
                icon={PaintBrush}
                title="No white labeling payments yet"
                description="Charges appear here after you activate or when monthly renewal runs."
              />
            ) : (
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead>Date</TableHead>
                    <TableHead>Type</TableHead>
                    <TableHead>Description</TableHead>
                    <TableHead className="text-right">Amount</TableHead>
                    <TableHead className="text-right">Balance after</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {payments.map((r) => (
                    <TableRow key={r.id} data-testid={`wl-pay-${r.id}`}>
                      <TableCell className="text-sm text-muted-foreground whitespace-nowrap">
                        {fmtDate(r.created_at)}
                      </TableCell>
                      <TableCell className="text-sm">
                        {TYPE_LABEL[r.txn_type] || r.txn_type}
                      </TableCell>
                      <TableCell className="text-sm">{r.description || "—"}</TableCell>
                      <TableCell className="text-right tabular-nums text-rose-600">
                        {money(r.amount_minor)}
                      </TableCell>
                      <TableCell className="text-right tabular-nums">
                        {money(r.balance_after_minor)}
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            )}
          </div>
        </>
      )}
    </div>
  );
}
